import { useEffect, useState } from "react";
import Introduction from "./introduction";
import HowWeTransferData from "./howWeTransferData";
import ThirdParty from "./thirdParty";
import ContactInformation from "./contact";
import DataWeCollect from "./dataWeCollect";
import WhyWeCollectData from "./whyWeCollectData";
import SharingYourInformation from "./sharingInfo";
import StorateOrRetentionPeriod from "./storageRetention";
import ChangesToPolicy from "./changeToPolicy";
import YourRights from "./yourRights";
import { useNavigate, useParams } from "react-router-dom";
import AccountDeletion from "./deleteAccount";
import SEOHead from "../seo/seo-header";

function PrivacyPolicy() {
  const date = new Date();
  const { section } = useParams<{ section?: string }>();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState(section || "introduction");

  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
  };
  const monthYear = date.toLocaleDateString("en-US", options);

  const formattedDate = monthYear.replace(" ", ", ");

  const termsTitle = [
    {
      id: "introduction",
      titleName: "Introduction",
    },
    {
      id: "data-we-collect",
      titleName: "Data We Collect",
    },
    {
      id: "why-we-collect-data",
      titleName: "Why We Collect and Use Your Personal Data",
    },
    {
      id: "sharing-information",
      titleName: "Sharing Your Information",
    },
    {
      id: "data-transfer-storage",
      titleName: "How we transfer, store and protect your data",
    },
    {
      id: "third-party-sites",
      titleName: "Third-Party Sites",
    },
    {
      id: "storage-retention",
      titleName: "Storage/Retention Period",
    },
    {
      id: "policy-changes",
      titleName: "Changes to this Policy",
    },
    {
      id: "your-rights",
      titleName: "Your Rights",
    },
    {
      id: "contact-us",
      titleName: "Contact Us",
    },
    {
      id: "account-deletion",
      titleName: "Account Deletion",
    },
  ];

  const scrollToSection = (sectionId: string) => {
    navigate(`/privacy-policy/${sectionId}`);
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100;
      const elementPosition =
        element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    const sectionId = section || "introduction";
    setActiveSection(sectionId);

    // Scroll after a small delay to ensure DOM is ready
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  }, [section]);

  const DeleteData = {
    "@context": "https://schema.org",
    "@type": "PrivacyPolicyPage",
    name: "Privacy Policy",
    description:
      "Contact Shiga for inquiries, support, and feedback. Reach out to our team for assistance.",
    url: "https://www.shiga.io/privacy-policy",
  };

  return (
    <>
      <SEOHead
        title="Privacy Policy - Shiga"
        description="Learn more about Shiga, our mission to revolutionize financial services, and meet our founding team."
        structuredData={DeleteData}
        canonicalUrl="https://www.shiga.io/privacy-policy"
      />

      <div className="bg-[white] h-full w-full pb-[250px] md:pb-[350px]">
        <div className="w-full pt-[100px] pb-[60px] bg-[#E6E6FF99]/25">
          <h1 className="text-[36px] font-ibm text-center text-primary-main leading-[50px] tracking-normal font-bold md:text-[25px]">
            PRIVACY POLICY
          </h1>
        </div>
        <div className="flex justify-center">
          <div className="max-w-[1300px] h-full pb-[50px]">
            <div className=" lg:px-6 xl:px-12 w-full h-full px-14 xlsm:px-3">
              <div className=" flex gap-[40px]">
                <div className="w-[35%] h-full md:hidden">
                  <div className="sticky top-[100px] cursor-pointer">
                    <p className="text-[#2a3342] text-[20px] leading-[30px] font-[600] mt-[40px] tracking-normal">
                      In this section{" "}
                    </p>
                    <div className="mt-[15px]">
                      {termsTitle.map((item) => {
                        return (
                          <div key={item.titleName}>
                            <p
                              onClick={() => scrollToSection(item.id)}
                              className={
                                activeSection === item.id
                                  ? `text-hbrand-500 text-[16px] bg-[#E6E6FF99]/25 py-4 px-4 leading-[150%] font-[600] tracking-normal mb-2`
                                  : `text-[16px] leading-[150%] text-[#556987] py-1 tracking-normal mb-2`
                              }
                            >
                              {item.titleName}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div className="w-[65%] md:w-full h-full">
                  <div className="w-full">
                    <div className="mt-[40px] flex items-center gap-[8px]">
                      <p className="text-[#556987] text-[14px] leading-[30px] font-[600]  tracking-normal">
                        By using the Shiga application and services you have
                        agreed to the terms detailed below. Kindly review
                        carefully.
                      </p>
                    </div>
                    <div className="mt-[20px] flex items-center gap-[8px]">
                      <div>
                        <img
                          src="/assets/calendar.svg"
                          alt="icon"
                          className="w-[20px] h-[20px]"
                        />
                      </div>
                      <p className="text-[#556987] text-[14px] leading-[30px] font-[600]  tracking-normal">
                        Last updated: {formattedDate}
                      </p>
                    </div>
                    <div id="introduction">
                      <Introduction />
                    </div>
                    <div id="data-we-collect">
                      <DataWeCollect />
                    </div>
                    <div id="why-we-collect-data">
                      <WhyWeCollectData />
                    </div>
                    <div id="sharing-information">
                      <SharingYourInformation />
                    </div>
                    <div id="data-transfer-storage">
                      <HowWeTransferData />
                    </div>
                    <div id="third-party-sites">
                      <ThirdParty />
                    </div>
                    <div id="storage-retention">
                      <StorateOrRetentionPeriod />
                    </div>
                    <div id="policy-changes">
                      <ChangesToPolicy />
                    </div>
                    <div id="your-rights">
                      <YourRights />
                    </div>
                    <div id="contact-us">
                      <ContactInformation />
                    </div>
                    <div id="account-deletion">
                      <AccountDeletion />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default PrivacyPolicy;
